import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CustomValidators } from '@app/core/utils/custom-validators';
import {
  PingMessages,
  PingMessagesPostBody,
  SupportedLanguages,
  TwoWaySetupService,
} from '@app/two-way/services/two-way-setup.service';
import { TitleService } from '@app/core/services/title.service';
import { ProductGroup } from '@app/core/models/client';
import { TwoWayConfig } from '@app/core/models/two-way-config';
import { MessageDialogComponent } from '@app/shared/message-dialog/message-dialog.component';
import { BlackoutUtils } from '@app/core/utils/blackout-utils';
import { BlackoutSettings, SmsClass } from '@app/core/models/message';
import { SmsService } from '@app/core/services/sms.service';
import { InputParamUtil } from '@app/core/models/input-param-util';
import { Observable, Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { NewFeedTransitionService } from '@app/core/services/new-feed-transition.service';
import { SessionService } from '@app/security/session.service';
import { Client } from '@app/core/models/client';
import { UnsavedChangesService } from '@app/core/services/unsaved-changes.service';
import { SetupBannerAutoresponseComponent } from '../setup-ooo-banner-autoresponse/setup-banner-autoresponse.component';
import { SpinnerComponent } from '../../../../shared/spinner/spinner.component';
import { NewGenericPhoneComponent } from '../../../../shared/new-generic-phone/new-generic-phone.component';
import { BubbleComponent } from '../../../../shared/bubble/bubble.component';
import { SmsBuilderComponent } from '../../../../shared/sms-builder/sms-builder.component';
import { MessageDialogComponent as MessageDialogComponent_1 } from '../../../../shared/message-dialog/message-dialog.component';
import { UnsavedChangesDialogComponent } from '../../../../shared/unsaved-changes-dialog/unsaved-changes-dialog.component';
import { FeatureFlags } from '@app/core/models/feature-flags';
import { FeatureService } from '@app/core/services/feature.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule, MatSelect } from '@angular/material/select';

/**
 * SmsClass, plus the two-way-specific migration flag, kept local to this
 * component (not on SmsClass itself, which message-builder also depends on).
 */
export type SmsWithDisabledFlag = SmsClass & { ping_sms_disabled?: boolean };

/** The per-language ping fields, in their sms-shaped naming. */
type PingMessageContent = Pick<SmsClass, 'body' | 'disclaimer' | 'alternate_text'>;

/**
 * Mirrors SmsBuilderComponent's `standardInputs` - the only dynamic values
 * this page's rn-sms-builder offers via autocomplete (it's never given
 * customInputs), so sms_body/sms_disclaimer shouldn't allow anything else.
 *
 * FE-only constraint for now: nothing on the backend enforces this same
 * allow-list at send/resolve time. Verified against current production data,
 * but this list can silently drift from what the backend actually supports.
 * Backend-side enforcement is planned as a follow-up.
 */
const STANDARD_PING_INPUT_PARAMS = [
  'account_ccid',
  'account_firstname',
  'account_lastname',
  'account_product_group',
  'account_secondary_account_id',
];

// Same FE-only allow-list caveat as STANDARD_PING_INPUT_PARAMS above
const AUTH_LINK_INPUT_PARAMS = [
  'auth-link',
];

@Component({
  selector: 'app-setup-sms-ping',
  templateUrl: './setup-sms-ping.component.html',
  styleUrls: ['./setup-sms-ping.component.scss'],
  standalone: true,
  imports: [SetupBannerAutoresponseComponent, SpinnerComponent, FormsModule, ReactiveFormsModule, NewGenericPhoneComponent, BubbleComponent, SmsBuilderComponent, MessageDialogComponent_1, MatFormFieldModule, MatSelectModule, UnsavedChangesDialogComponent]
})
export class SetupSmsPingComponent implements OnInit, OnDestroy {
  @ViewChild('smsPingMessage', { static: true })
  smsPingMessage: MessageDialogComponent;
  @ViewChild('dynamicInputsMessage', { static: true })
  dynamicInputsMessage: MessageDialogComponent;
  @ViewChild('unsavedChangesDialog', { static: true })
  unsavedChangesDialog: UnsavedChangesDialogComponent;
  @ViewChild('languageSelect')
  languageSelect: MatSelect;

  blackoutWindowMessage: string;
  twoWayConfig: TwoWayConfig;
  defaultProductGroup: ProductGroup;
  smsPingForm: UntypedFormGroup;
  currentClient: Client;
  clientId: string;
  featureFlags = FeatureFlags;
  languageCode: string = 'en';
  supportedLanguages: SupportedLanguages[] = [];
  isErrorSupportedLanguages: boolean = false;
  pingMessages!: PingMessages;
  isFallbackPingMessages = false;
  isErrorPingMessages: boolean = false;
  /**
   * Sms-shaped view model bound to rn-sms-builder when translations are
   * enabled. Its auth_link/is_shown/blackout_settings mirror twoWayConfig.sms
   * (client-level, not per-language); its body/disclaimer/alternate_text/
   * is_alternate_message come from the currently selected language's
   * pingMessages so twoWayConfig.sms itself is never overwritten with
   * translated content.
   */
  pingSmsModel!: SmsClass;
  /**
   * The English ping content currently persisted, used as the fallback
   * placeholder for languages with no translation saved yet. Tracked
   * separately from twoWayConfig.sms because saving English writes to
   * pingMessages only — twoWayConfig.sms keeps its pre-migration text and so
   * goes stale as soon as the English ping messages are edited and saved.
   */
  private englishPingContent: PingMessageContent | null = null;
  private pendingLanguageCode: string | null = null;
  private pendingNavigation: Subject<boolean> | null = null;
  private readonly unsavedChangesHandler: () => Observable<boolean> | boolean;

  constructor(
    private titleService: TitleService,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private smsService: SmsService,
    private twoWaySetupService: TwoWaySetupService,
    public newFeed: NewFeedTransitionService,
    private sessionService: SessionService,
    public featureService: FeatureService,
    private unsavedChangesService: UnsavedChangesService,
  ) {
    this.unsavedChangesHandler = this.canDeactivate.bind(this);
  }

  ngOnInit() {
    this.currentClient = this.sessionService.getCurrentUsersClient();
    this.clientId = this.currentClient.id;
    this.titleService.activate(
      this.currentClient.feed_enabled
        ? 'Relay Messenger Setup'
        : 'Two Way Messaging Setup',
      this.newFeed.enabled ? 'Notification Ping' : 'SMS Ping',
    );
    if (this.isRMPingTranslationsEnabled) {
      this.unsavedChangesService.register(this.unsavedChangesHandler);
      this.twoWaySetupService.getSupportedLanguages(this.currentClient.id).subscribe({
        next: (supportedLanguages) => {
          this.supportedLanguages = supportedLanguages;
          this.isErrorSupportedLanguages = false;
        },
        error: () => {
          this.isErrorSupportedLanguages = true;
          this.supportedLanguages = [{ code: 'en', name: 'English' }];
        },
      });
      this.fetchPingMessages(this.languageCode);
    }

    this.twoWaySetupService
      .getTwoWayConfig()
      .pipe(
        switchMap((twoWayConfig) => {
          this.twoWayConfig = twoWayConfig;
          return this.twoWaySetupService.getClient();
        }),
        switchMap((client) => {
          // if twoWayConfig doesn't have a blackout Window, set one using the client default
          if (
            !this.twoWayConfig.sms.blackout_settings &&
            !this.twoWayConfig.sms.blackout_settings.blackout_window
          ) {
            this.twoWayConfig.sms.blackout_settings = new BlackoutSettings(
              client.blackout_window,
            );
          }
          this.setBlackoutWindowMessage(client);
          this.initSmsPingForm();
          this.syncPingMessagesToUi();

          return this.twoWaySetupService.getDefaultProductGroup();
        }),
      )
      .subscribe((defaultProductGroup) => {
        this.smsService.init(this.twoWayConfig.sms, defaultProductGroup);
        this.defaultProductGroup = defaultProductGroup;
      });
  }

  ngOnDestroy() {
    if (this.isRMPingTranslationsEnabled) {
      this.unsavedChangesService.unregister(this.unsavedChangesHandler);
    }
    this.titleService.deactivate();
  }

  isLoading(): boolean {
    return this.smsPingForm === undefined;
  }

  saveAndContinue(skipWarning: boolean = false) {
    const conversationOrSummary = this.newFeed.enabled ? 'conversations' : 'summary';
    if (!this.smsPingForm.valid) {
      return;
    }

    this.updateConfigWithFormData(); // has to come before alternateSmsRequired() check
    if (!this.pingSmsModel) {
      this.pingSmsModel = new SmsClass({ showSmsByDefault: true });
    }
    this.pingSmsModel.body = this.smsBodyCtl.value;
    this.pingSmsModel.disclaimer = this.smsDisclaimerCtl.value;
    this.pingSmsModel.alternate_text = this.alternateSmsTextCtl.value;
    this.pingSmsModel.is_alternate_message = this.twoWayConfig.sms.is_alternate_message;

    if (this.alternateSmsRequired() && !skipWarning) {
      this.dynamicInputsMessage.showMessage();
      return;
    }

    this.twoWayConfig.sms.text = `${this.smsBodyCtl.value} @{auth-link} ${this.smsDisclaimerCtl.value}`;
    this.savePingMessages(
      () => {
        // Save legacy twoWayConfig concurrently or sequentially, then navigate
        this.twoWaySetupService.saveTwoWayConfig(this.twoWayConfig).subscribe({
          next: () => {
            this.router.navigateByUrl(
              `/two-way/setup/${conversationOrSummary}`,
            );
          },
          error: () => {
            this.smsPingMessage.showMessage(
              `We're sorry, there was an error saving your information.`,
            );
          },
        });
      },
      undefined,
      false,
    );
  }

  /**
   * Saves the currently viewed language's pingMessages. auth_link/is_shown/
   * blackout_settings are client-level and unaffected by language selection
   */
  saveWithoutNavigation(skipWarning: boolean = false) {
    this.applyAlternateTextRequiredState();
    if (!this.smsPingForm.valid) {
      this.revealPingMessageValidationErrors();
      return;
    }

    if (this.alternateSmsRequired() && !skipWarning) {
      this.dynamicInputsMessage.showMessage();
      return;
    }

    this.savePingMessages(() => {
      this.smsPingMessage.showMessage(`Your changes have been saved successfully.`);
    }, undefined, false);
  }

  private savePingMessages(
    onSuccess: () => void,
    onError?: () => void,
    showSuccessMessage = false,
  ) {
    // Snapshot the content as it is at request time — rn-sms-builder keeps
    // mutating pingSmsModel while the POST is in flight, so re-reading it in
    // the success handler could capture an edit that wasn't persisted.
    const savedContent = this.toPingMessageContent(this.pingSmsModel);
    const pingMessagesBody: PingMessagesPostBody = {
      pingMessages: {
        notificationText: savedContent.body,
        disclaimer: savedContent.disclaimer,
        alternateNotificationText: savedContent.alternate_text,
        alternateNotificationTextEnabled: this.pingSmsModel.is_alternate_message ?? false, // set is_alternate_message to false by default since most clients don't include this in the sms object and it is a required field in the new twoway table
      },
    };

    this.twoWaySetupService
      .savePingMessagesWithLanguages(
        this.currentClient.id,
        pingMessagesBody,
        this.languageCode,
      )
      .subscribe({
        next: () => {
          if (this.languageCode === 'en') {
            // Keep the placeholder fallback in step with what was just
            // persisted — twoWayConfig.sms isn't written by this endpoint.
            this.englishPingContent = savedContent;
            this.markLegacySmsDisabled();
          }
          this.resetPingFormControlStates();
          if (showSuccessMessage) {
            this.smsPingMessage.showMessage(`Your changes have been saved successfully.`);
          }
          onSuccess();
        },
        error: () => {
          this.smsPingMessage.showMessage(
            `We're sorry, there was an error saving your information.`,
          );
          onError?.();
        },
      });
  }

  /**
   * Marks twoWayConfig.sms as migrated once the English pingMessages have
   * been saved for the first time
   */
  private markLegacySmsDisabled() {
    if (this.legacySms.ping_sms_disabled) {
      return;
    }
    this.legacySms.ping_sms_disabled = true;
    this.twoWaySetupService.saveTwoWayConfig(this.twoWayConfig).subscribe({
      next: () => { },
      error: () => { },
    });
  }

  /**
 * Handles confirmation from the shared dynamicInputsMessage dialog, which
 * both saveAndContinue and saveWithoutNavigation can open — routes to
 * whichever save flow is actually active rather than assuming one.
 */
  onDynamicInputsAffirm() {
    if (this.isRMPingTranslationsEnabled) {
      this.saveWithoutNavigation(true);
      return;
    }
    this.saveAndContinue(true);
  }

  get smsBodyCtl(): AbstractControl {
    return this.smsPingForm.get('sms_body');
  }

  get smsDisclaimerCtl(): AbstractControl {
    return this.smsPingForm.get('sms_disclaimer');
  }

  get alternateSmsTextCtl(): AbstractControl {
    return this.smsPingForm.get('alternate_sms_text');
  }

  get fullSmsText(): string {
    return this.smsService.getFullSmsBody(
      this.smsBodyCtl.value,
      this.smsDisclaimerCtl.value,
    );
  }

  /**
  * Determines if the "RM Translations with Languages" is enabled for the user
  */
  get rmTranslationsWithLanguagesFlagOn(): boolean {
    return this.featureService.checkFlag(this.featureFlags.rm_multi_lingual);
  }

  get isTranslationsEnabled(): boolean {
    return (
      this.rmTranslationsWithLanguagesFlagOn &&
      !!this.currentClient?.two_way_config?.translations_enabled
    );
  }
  /**
   * Determines if the "RM Ping Translations" is enabled for the user
   */
  get isRMPingTranslationsEnabled(): boolean {
    return this.featureService.checkFlag(this.featureFlags.rm_ping_translations);
  }

  /**
   * The sms-shaped model that rn-sms-builder should read from: pingSmsModel
   * (translated content) when translations are enabled, otherwise
   * twoWayConfig.sms directly.
   */
  get currentSmsModel(): SmsClass {
    return this.isRMPingTranslationsEnabled && this.pingSmsModel
      ? this.pingSmsModel
      : this.twoWayConfig?.sms;
  }

  private get legacySms(): SmsWithDisabledFlag {
    return this.twoWayConfig.sms as SmsWithDisabledFlag;
  }

  onLanguageChange(languageCode: string) {
    if (!this.isRMPingTranslationsEnabled || languageCode === this.languageCode) {
      return;
    }

    if (this.smsPingForm?.dirty) {
      this.pendingLanguageCode = languageCode;
      this.languageSelect?.writeValue(this.languageCode);
      this.languageSelect?.close();
      this.openUnsavedChangesDialog();
      return;
    }

    this.switchLanguage(languageCode);
  }

  /*
    Guard to prevent navigation away from the SetupSmsPingComponent
    if there are unsaved changes.
  */
  canDeactivate(): Observable<boolean> | boolean {
    if (this.unsavedChangesService.consumeAllowNextNavigation()) {
      return true;
    }
    if (!this.isRMPingTranslationsEnabled) {
      return true;
    }
    if (!this.smsPingForm || !this.hasUnsavedPingMessageChanges()) {
      return true;
    }
    if (this.pendingNavigation) {
      return this.pendingNavigation.asObservable();
    }
    this.pendingNavigation = new Subject<boolean>();
    this.pendingLanguageCode = null;
    this.openUnsavedChangesDialog();
    return this.pendingNavigation.asObservable();
  }

  onUnsavedChangesSave() {
    if (this.pendingNavigation) {
      if (!this.smsPingForm.valid) {
        return;
      }
      this.savePingMessages(() => {
        this.resolvePendingNavigation(true);
      }, () => {
        this.resolvePendingNavigation(false);
      });
      return;
    }

    if (!this.pendingLanguageCode) {
      return;
    }
    const nextLanguage = this.pendingLanguageCode;
    this.pendingLanguageCode = null;

    this.savePingMessages(() => {
      this.switchLanguage(nextLanguage);
    }, undefined, true);
  }

  onUnsavedChangesDiscard() {
    if (this.pendingNavigation) {
      this.resolvePendingNavigation(true);
      return;
    }
    if (!this.pendingLanguageCode) {
      return;
    }
    const nextLanguage = this.pendingLanguageCode;
    this.pendingLanguageCode = null;
    this.switchLanguage(nextLanguage);
  }

  onUnsavedChangesDismiss() {
    if (this.pendingNavigation) {
      this.resolvePendingNavigation(false);
    }
    this.pendingLanguageCode = null;
  }

  private openUnsavedChangesDialog() {
    const title = 'Changes not saved';
    const messageBody =
      'The changes you have made to the ping messages have not been saved.\n' +
      'Would you like to save before leaving this page?';
    this.unsavedChangesDialog.open(title, messageBody);
  }

  private switchLanguage(languageCode: string) {
    this.languageCode = languageCode;
    this.fetchPingMessages(languageCode);
  }

  private resolvePendingNavigation(allow: boolean) {
    if (!this.pendingNavigation) {
      return;
    }
    this.pendingNavigation.next(allow);
    this.pendingNavigation.complete();
    this.pendingNavigation = null;
  }

  private hasUnsavedPingMessageChanges(): boolean {
    if (!this.isRMPingTranslationsEnabled) {
      return false;
    }
    return !!(
      this.smsBodyCtl?.dirty ||
      this.smsDisclaimerCtl?.dirty ||
      this.alternateSmsTextCtl?.dirty
    );
  }

  private resetPingFormControlStates() {
    const controls = [this.smsBodyCtl, this.smsDisclaimerCtl, this.alternateSmsTextCtl];
    controls.forEach((control) => {
      control?.markAsPristine({ onlySelf: true });
      control?.markAsUntouched({ onlySelf: true });
      control?.updateValueAndValidity({ onlySelf: true });
    });
    this.smsPingForm?.markAsPristine({ onlySelf: true });
    this.smsPingForm?.markAsUntouched({ onlySelf: true });
    this.smsPingForm?.updateValueAndValidity({ onlySelf: true });
  }

  alternateSmsRequired(): boolean {
    const smsModel = this.currentSmsModel;
    // Only the fields actually shown to the recipient matter here
    const smsInputParams = InputParamUtil.findInputParametersForObject({
      body: smsModel.body,
      disclaimer: smsModel.disclaimer,
    });
    return (
      smsModel.is_shown === 'true' &&
      smsInputParams.length > 0 &&
      (!smsModel.is_alternate_message ||
        smsModel.alternate_text.trim().length === 0)
    );
  }

  /**
   * When configuring a non-English language, an empty notification/
   * disclaimer/alternate-text field falls back to English until translated —
   * shown as a grayed-out placeholder of the currently persisted English
   * content (englishPingContent), which tracks the English pingMessages
   * across saves. twoWayConfig.sms is only the fallback for when the English
   * pingMessages were never loaded (e.g. their fetch failed).
   */
  getPingMessagePlaceholder(field: 'body' | 'disclaimer' | 'alternate_text'): string {
    if (!this.isRMPingTranslationsEnabled || this.languageCode === 'en') {
      return '';
    }
    const currentValue = this.getPingMessageFieldCtl(field)?.value;
    if (this.isNonEmptyString(currentValue)) {
      return '';
    }
    const englishContent = this.englishPingContent ?? this.twoWayConfig?.sms;
    return englishContent?.[field] ?? '';
  }

  private getPingMessageFieldCtl(
    field: 'body' | 'disclaimer' | 'alternate_text',
  ): AbstractControl {
    switch (field) {
      case 'body':
        return this.smsBodyCtl;
      case 'disclaimer':
        return this.smsDisclaimerCtl;
      case 'alternate_text':
        return this.alternateSmsTextCtl;
    }
  }

  /**
   * Narrows an sms-shaped object down to just the three per-language ping
   * fields, so the two places that refresh englishPingContent (a save, and a
   * sync from the fetched pingMessages) can't drift apart on which fields
   * count as ping content.
   */
  private toPingMessageContent(source: PingMessageContent): PingMessageContent {
    return {
      body: source.body,
      disclaimer: source.disclaimer,
      alternate_text: source.alternate_text,
    };
  }

  private isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private toBoolean(value: unknown): boolean {
    return value === true || value === 'true';
  }

  private setBlackoutWindowMessage(client) {
    const clientBlackoutWindow = client.blackout_window;
    const weekendBlackoutMessage = BlackoutUtils.weekendBlackoutMessage(
      clientBlackoutWindow,
      'Messages will also not be sent on ',
    );
    this.blackoutWindowMessage = `Client Blackout window applies.  Messages will not be sent between ${BlackoutUtils.getDisplayHour(
      clientBlackoutWindow.start_time,
    )} and ${BlackoutUtils.getDisplayHour(
      clientBlackoutWindow.end_time,
    )} Eastern Standard Time.  ${weekendBlackoutMessage}`;
  }

  private fetchPingMessages(languageCode: string) {
    this.twoWaySetupService
      .getPingMessagesWithLanguages(this.currentClient.id, languageCode)
      .subscribe({
        next: ({ pingMessages, isFallback }) => {
          this.pingMessages = pingMessages;
          this.isFallbackPingMessages = isFallback;
          this.isErrorPingMessages = false;
          this.syncPingMessagesToUi();
        },
        error: () => {
          this.isErrorPingMessages = true;
        },
      });
  }

  /**
   * Rebuilds pingSmsModel from the currently fetched pingMessages
   *
   * English is a special case until legacySms.ping_sms_disabled marks the migration
   * as complete: twoWayConfig.sms already holds the real (pre-migration)
   * English content, so it's used as-is rather than pingMessages. Once
   * disabled is set, pingMessages becomes authoritative for English too
   */
  private syncPingMessagesToUi() {
    if (!this.isRMPingTranslationsEnabled || !this.twoWayConfig || !this.pingMessages) {
      return;
    }
    const base = this.pingSmsModel ?? this.twoWayConfig.sms;
    const useLegacyEnglishContent =
      this.languageCode === 'en' && !this.legacySms.ping_sms_disabled;

    const source = useLegacyEnglishContent
      ? {
        body: this.twoWayConfig.sms.body,
        disclaimer: this.twoWayConfig.sms.disclaimer,
        alternate_text: this.twoWayConfig.sms.alternate_text,
        is_alternate_message: this.toBoolean(this.twoWayConfig.sms.is_alternate_message),
      }
      : {
        body: this.pingMessages.notificationText,
        disclaimer: this.pingMessages.disclaimer,
        alternate_text: this.pingMessages.alternateNotificationText,
        is_alternate_message: this.toBoolean(this.pingMessages.alternateNotificationTextEnabled),
      };

    if (this.languageCode === 'en') {
      this.englishPingContent = this.toPingMessageContent(source);
    }

    this.pingSmsModel = Object.assign(
      new SmsClass({ showSmsByDefault: true }),
      base,
      source,
    );

    if (this.smsPingForm) {
      this.smsPingForm.patchValue({
        sms_body: source.body,
        sms_disclaimer: source.disclaimer,
        alternate_sms_text: source.alternate_text,
      });
      // sms-builder's toggleAlternateSMS() only runs on its own init and on
      // the radio button's own click — it never re-syncs when pingSmsModel
      // is swapped out wholesale by a language switch, so mirror it here.
      if (this.pingSmsModel.is_alternate_message) {
        this.alternateSmsTextCtl.enable({ onlySelf: true });
      } else {
        this.alternateSmsTextCtl.disable({ onlySelf: true });
      }
      this.resetPingFormControlStates();
    }
  }

  private updateConfigWithFormData() {
    const formData = this.smsPingForm.getRawValue();
    this.twoWayConfig.sms.body = formData['sms_body'];
    this.twoWayConfig.sms.disclaimer = formData['sms_disclaimer'];
    this.twoWayConfig.sms.alternate_text = formData['alternate_sms_text'];
  }

  private initSmsPingForm() {
    this.smsPingForm = this.formBuilder.group({
      alternate_sms_text: [
        this.twoWayConfig.sms.alternate_text,
        [
          CustomValidators.validateAuthLinkParamOnly(
            AUTH_LINK_INPUT_PARAMS,
          ),
        ],
      ],
      sms_body: [
        this.twoWayConfig.sms.body,
        [
          Validators.required,
          CustomValidators.validateStandardInputParamsOnly(
            STANDARD_PING_INPUT_PARAMS,
          ),
        ],
      ],
      auth_link: [],
      sms_disclaimer: [
        this.twoWayConfig.sms.disclaimer,
        [
          Validators.required,
          CustomValidators.validateStandardInputParamsOnly(
            STANDARD_PING_INPUT_PARAMS,
          ),
        ],
      ],
    });
  }

  /**
   * Marks the required ping message fields touched/dirty so their
   * app-error-display messages render, without disabling the Save button —
   * clicking Save while invalid should show what's missing, not just no-op.
   */
  private revealPingMessageValidationErrors() {
    [this.smsBodyCtl, this.smsDisclaimerCtl, this.alternateSmsTextCtl].forEach((control) => {
      if (control?.invalid) {
        control.markAsTouched({ onlySelf: true });
        control.markAsDirty({ onlySelf: true });
      }
    });
  }

  /**
   * alternate_sms_text has no Validators.required of its own — sms-builder's
   * toggleAlternateSMS() only enables/disables it based on is_alternate_message,
   * it never sets a validator. Enforce "required when enabled" here at
   * save-time via setErrors, since is_alternate_message is a plain property
   * (not a form control) mutated inside the shared SmsBuilderComponent, so
   * there's no valueChanges to hook a reactive validator to. Uses the same
   * toggledButNoValue error key as CustomValidators.toggledOnRequiresValue
   * so app-error-display shows the existing "required when toggle is
   * enabled" message.
   */
  private applyAlternateTextRequiredState() {
    const control = this.alternateSmsTextCtl;
    if (!control) {
      return;
    }
    const required = this.toBoolean(this.pingSmsModel?.is_alternate_message);
    const isEmpty = !this.isNonEmptyString(control.value);

    if (required && isEmpty) {
      control.setErrors({ ...control.errors, toggledButNoValue: true });
      return;
    }

    if (control.errors?.['toggledButNoValue']) {
      const { toggledButNoValue: _removedError, ...remainingErrors } = control.errors;
      control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
    }
  }
}
